import { useState } from "react";
import { t } from "ttag";

import { CompoundedMedicationDisclaimer } from "@/features/products/components/compounded-medications-disclaimer";
import type { SelectedProduct } from "@/features/products/context";
import {
  CATEGORY_COLOR,
  getDisabledReasonLabel,
} from "@/features/products/models";
import {
  Accordion,
  AddIcon,
  Alert,
  Badge,
  Button,
  Dialog,
  Heading,
  MarkdownContent,
  ScrollArea,
  ShowcaseCard,
} from "@/shared/ui";
import { MinusIcon } from "@/shared/ui/icons";

interface ExpandedProductCardProps {
  product: SelectedProduct;
  onContinue: VoidFunction;
  onHoverContinue?: VoidFunction;
}

const PRODUCT_DETAIL = {
  FEATURES: "features",
  IMPORTANT_INFORMATION: "important-information",
  COMMON_DISQUALIFIERS: "common-disqualifiers",
};

const AccordionIcon = ({ expanded }: { expanded: boolean }) => (
  <div
    data-slot="accordion-icon"
    className="rounded-full p-1 text-secondary-500 transition-all duration-150 [&>svg]:size-4"
  >
    {expanded ? <MinusIcon /> : <AddIcon />}
  </div>
);

export const ExpandedProductCard = ({
  product,
  onContinue,
  onHoverContinue,
}: ExpandedProductCardProps) => {
  const [accordionValue, setAccordionValue] = useState(PRODUCT_DETAIL.FEATURES);

  const isProductDisabled = Boolean(product.disabled?.length);

  return (
    <div className="flex h-full flex-col gap-3 sm:grid sm:grid-cols-[2fr_3fr] sm:grid-rows-[1fr_auto] sm:gap-x-6 sm:gap-y-12">
      <ShowcaseCard.Inset className="relative row-span-2 m-0">
        <ShowcaseCard.Thumbnail
          src={product.thumbnail ?? "#"}
          className="rounded-xl sm:h-120"
        />

        <ShowcaseCard.Badges className="absolute top-4 left-4 w-2/3">
          {product.priceTag && (
            <Badge size="small" color="light-primary">
              {product.priceTag}
            </Badge>
          )}
        </ShowcaseCard.Badges>
      </ShowcaseCard.Inset>

      <ScrollArea>
        <Accordion.Root
          type="single"
          collapsible
          className="md:max-h-48"
          value={accordionValue}
          onValueChange={(value) =>
            setAccordionValue(value || PRODUCT_DETAIL.FEATURES)
          }
        >
          <Accordion.Item
            value={PRODUCT_DETAIL.FEATURES}
            className="flex flex-col border-neutral-100"
          >
            <Accordion.Trigger className="flex items-start gap-2 pb-4 text-start transition-all [&>[data-slot=accordion-icon]]:translate-y-1 hover:[&>[data-slot=accordion-icon]]:bg-primary-50">
              <div className="inline-flex grow flex-wrap items-center justify-between gap-2">
                <Heading size="medium">{product.name}</Heading>

                <Badge
                  size="small"
                  outline
                  color={CATEGORY_COLOR[product.category.slug]}
                >
                  <MarkdownContent>{product.description}</MarkdownContent>
                </Badge>
              </div>

              <AccordionIcon
                expanded={accordionValue === PRODUCT_DETAIL.FEATURES}
              />
            </Accordion.Trigger>

            <Accordion.Content className="pb-4">
              <ShowcaseCard.Description>
                <MarkdownContent>{product.features}</MarkdownContent>
              </ShowcaseCard.Description>
            </Accordion.Content>
          </Accordion.Item>

          <Accordion.Item
            value={PRODUCT_DETAIL.IMPORTANT_INFORMATION}
            className="flex flex-col border-t border-neutral-100"
          >
            <Accordion.Trigger className="py-4 font-semibold text-neutral-800 hover:[&>[data-slot=accordion-icon]]:bg-primary-50">
              {t`Important information`}

              <AccordionIcon
                expanded={
                  accordionValue === PRODUCT_DETAIL.IMPORTANT_INFORMATION
                }
              />
            </Accordion.Trigger>

            <Accordion.Content className="pb-4">
              <ShowcaseCard.Description>
                <MarkdownContent>{product.disclaimer}</MarkdownContent>
              </ShowcaseCard.Description>
            </Accordion.Content>
          </Accordion.Item>

          {product.commonDisqualifiers && (
            <Accordion.Item
              value={PRODUCT_DETAIL.COMMON_DISQUALIFIERS}
              className="flex flex-col border-t border-neutral-100"
            >
              <Accordion.Trigger className="py-4 font-semibold text-neutral-800 hover:[&>[data-slot=accordion-icon]]:bg-primary-50">
                {t`Common disqualifiers`}

                <AccordionIcon
                  expanded={
                    accordionValue === PRODUCT_DETAIL.COMMON_DISQUALIFIERS
                  }
                />
              </Accordion.Trigger>

              <Accordion.Content className="pb-4">
                <ShowcaseCard.Description className="[&_em]:text-neutral-800">
                  <MarkdownContent>
                    {product.commonDisqualifiers}
                  </MarkdownContent>
                </ShowcaseCard.Description>
              </Accordion.Content>
            </Accordion.Item>
          )}
        </Accordion.Root>
      </ScrollArea>

      <CompoundedMedicationDisclaimer />
      {isProductDisabled && product.disabled?.[0] && (
        <Alert className="col-start-2 rounded-xl border-red-200 bg-red-100 text-red-700">
          <h3 className="text-sm font-semibold text-red-800">{t`Not available`}</h3>
          <p className="text-base">
            {getDisabledReasonLabel(product.disabled[0].type)}
          </p>
        </Alert>
      )}

      <Dialog.Footer className="col-span-2 sm:grid sm:grid-cols-[2fr_3fr] sm:gap-6">
        <Dialog.Close asChild>
          <Button size="lg" variant="secondary" className="col-span-1">
            {t`Keep browsing`}
          </Button>
        </Dialog.Close>

        <Dialog.Close asChild>
          <Button
            size="lg"
            onClick={onContinue}
            onMouseEnter={onHoverContinue}
            disabled={isProductDisabled}
          >
            {isProductDisabled ? t`Product unavailable` : t`Select`}
          </Button>
        </Dialog.Close>
      </Dialog.Footer>
    </div>
  );
};
